import { Typography } from 'antd';
import { FC, useCallback, useMemo, useRef } from 'react';

import { IHealthFlagRange } from '../../../dashboard/utils/healthFlags';

const { Text } = Typography;

interface IHealthFlagSliderProps {
  label: string;
  description: string;
  range: IHealthFlagRange;
  onChange: (range: IHealthFlagRange) => void;
  min: number;
  max: number;
  step?: number;
  unit: string;
  lowerBetter: boolean;
}

const STATUS_COLORS = {
  up: '#00E5A0',
  warn: '#c6c300',
  down: '#E8008A',
};

const HANDLE_STYLE: React.CSSProperties = {
  position: 'absolute',
  top: '50%',
  transform: 'translate(-50%, -50%)',
  width: 15,
  height: 30,
  borderRadius: 4,
  backgroundColor: '#4f6d7a',
  border: '2px solid #fff',
  cursor: 'ew-resize',
  zIndex: 2,
};

const HealthFlagSlider: FC<IHealthFlagSliderProps> = ({
  label,
  description,
  range,
  onChange,
  min,
  max,
  step = 1,
  unit,
  lowerBetter,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  // Two handle positions as percentages (0-100)
  const sliderValue: [number, number] = useMemo(() => {
    const totalRange = max - min;
    if (totalRange <= 0) return [0, 100];
    const clamp = (v: number) =>
      Math.max(0, Math.min(100, ((v - min) / totalRange) * 100));
    if (lowerBetter) {
      return [clamp(range.upTo), clamp(range.warnTo)];
    }
    return [clamp(range.downFrom), clamp(range.upTo)];
  }, [range, lowerBetter, min, max]);

  // Colors for the 3 segments (left to right)
  const segmentColors = lowerBetter
    ? [STATUS_COLORS.up, STATUS_COLORS.warn, STATUS_COLORS.down]
    : [STATUS_COLORS.down, STATUS_COLORS.warn, STATUS_COLORS.up];

  const pctToValue = useCallback(
    (pct: number) => {
      const totalRange = max - min;
      const raw = min + (pct / 100) * totalRange;
      return Math.round(raw / step) * step;
    },
    [min, max, step]
  );

  const createDragHandler = useCallback(
    (handleIndex: 0 | 1) => (e: React.MouseEvent) => {
      e.preventDefault();
      const container = containerRef.current;
      if (!container) return;
      const startX = e.clientX;
      const startPct = sliderValue[handleIndex];
      const containerWidth = container.offsetWidth;

      const onMouseMove = (moveEvent: MouseEvent) => {
        const delta = moveEvent.clientX - startX;
        const deltaPct = (delta / containerWidth) * 100;
        let newPct = startPct + deltaPct;

        // Clamp: handle 0 can go from 0 to handle1-1%, handle 1 from handle0+1% to 100
        if (handleIndex === 0) {
          newPct = Math.max(0, Math.min(newPct, sliderValue[1] - 1));
        } else {
          newPct = Math.max(sliderValue[0] + 1, Math.min(newPct, 100));
        }

        const val = pctToValue(newPct);

        if (lowerBetter) {
          if (handleIndex === 0) {
            onChange({
              upFrom: min,
              upTo: val,
              warnFrom: val,
              warnTo: range.warnTo,
              downFrom: range.downFrom,
              downTo: max,
            });
          } else {
            onChange({
              upFrom: min,
              upTo: range.upTo,
              warnFrom: range.warnFrom,
              warnTo: val,
              downFrom: val,
              downTo: max,
            });
          }
        } else {
          if (handleIndex === 0) {
            onChange({
              downFrom: val,
              downTo: min,
              warnFrom: val,
              warnTo: range.warnTo,
              upFrom: max,
              upTo: range.upTo,
            });
          } else {
            onChange({
              downFrom: range.downFrom,
              downTo: min,
              warnFrom: range.warnFrom,
              warnTo: val,
              upFrom: max,
              upTo: val,
            });
          }
        }
      };

      const onMouseUp = () => {
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
      };

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    },
    [sliderValue, lowerBetter, min, max, range, onChange, pctToValue]
  );

  // Zone labels
  const zones = lowerBetter
    ? [
        {
          color: STATUS_COLORS.up,
          label: `UP: ${range.upFrom}–${range.upTo}${unit}`,
          pct: sliderValue[0],
        },
        {
          color: STATUS_COLORS.warn,
          label: `WARN: ${range.warnFrom}–${range.warnTo}${unit}`,
          pct: sliderValue[1] - sliderValue[0],
        },
        {
          color: STATUS_COLORS.down,
          label: `DOWN: ${range.downFrom}+${unit}`,
          pct: 100 - sliderValue[1],
        },
      ]
    : [
        {
          color: STATUS_COLORS.down,
          label: `DOWN: ${range.downTo}–${range.downFrom}${unit}`,
          pct: sliderValue[0],
        },
        {
          color: STATUS_COLORS.warn,
          label: `WARN: ${range.warnFrom}–${range.warnTo}${unit}`,
          pct: sliderValue[1] - sliderValue[0],
        },
        {
          color: STATUS_COLORS.up,
          label: `UP: ${range.upTo}–${range.upFrom}${unit}`,
          pct: 100 - sliderValue[1],
        },
      ];

  return (
    <div
      style={{
        marginBottom: 24,
        paddingBottom: 24,
        borderBottom: '1px solid #f0f0f0',
      }}
    >
      <Text strong style={{ display: 'block', marginBottom: 4, fontSize: 14 }}>
        {label}
      </Text>
      <Text
        type="secondary"
        style={{ display: 'block', marginBottom: 12, fontSize: 12 }}
      >
        {description}
      </Text>

      <div style={{ padding: '0 8px' }}>
        {/* Custom slider bar with handles */}
        <div
          ref={containerRef}
          style={{
            position: 'relative',
            height: 16,
            marginTop: 10,
            marginBottom: 10,
          }}
        >
          {/* Colored bar container */}
          <div
            style={{
              display: 'flex',
              height: '100%',
              borderRadius: 4,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                width: `${sliderValue[0]}%`,
                backgroundColor: segmentColors[0],
                height: '100%',
              }}
            />
            <div
              style={{
                width: `${sliderValue[1] - sliderValue[0]}%`,
                backgroundColor: segmentColors[1],
                height: '100%',
              }}
            />
            <div
              style={{
                width: `${100 - sliderValue[1]}%`,
                backgroundColor: segmentColors[2],
                height: '100%',
              }}
            />
          </div>

          {/* Handle 1 */}
          <div
            style={{ ...HANDLE_STYLE, left: `${sliderValue[0]}%` }}
            onMouseDown={createDragHandler(0)}
          />

          {/* Handle 2 */}
          <div
            style={{ ...HANDLE_STYLE, left: `${sliderValue[1]}%` }}
            onMouseDown={createDragHandler(1)}
          />
        </div>

        {/* Zone labels below the bar */}
        <div
          style={{
            display: 'flex',
            position: 'relative',
            height: 24,
            marginTop: 8,
          }}
        >
          {zones.map((z, i) => (
            <div
              key={i}
              style={{
                position: 'absolute',
                left:
                  i === 0
                    ? '0%'
                    : i === 1
                    ? `${sliderValue[0]}%`
                    : `${sliderValue[1]}%`,
                width: `${z.pct}%`,
                textAlign: 'center',
              }}
            >
              <Text
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: z.color,
                  letterSpacing: '0.08em',
                }}
              >
                {z.label}
              </Text>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default HealthFlagSlider;
